import { skipToken } from '@reduxjs/toolkit/query';
import {
    EnrollmentStatusIndicator,
    type OnboardingStatus,
    OnboardingStatusIndicator,
    ReferralStatusIndicator,
} from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PLATFORM_ELIGIBILITY_PENDING } from 'services/memberEligibilityStatus';
import { useGetOnboardingStatusQuery } from 'store/api/onboardingApi';

type Status =
    (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];

interface ReferralAndOnboardingStatusIndicatorProps {
    referralStatus: Status;
    enrollmentStatus?: string;
    onboardingStatus?: OnboardingStatus;
    referralId?: string;
    skipFetch?: boolean;
}

export const ReferralAndOnboardingStatusIndicator = ({
    referralStatus,
    enrollmentStatus = undefined,
    onboardingStatus = undefined,
    referralId = undefined,
    skipFetch = false,
}: ReferralAndOnboardingStatusIndicatorProps) => {
    const { marketplaceEnhancedEligibility } = useFlags<{
        marketplaceEnhancedEligibility: boolean;
    }>();

    const canShowOnboardingStatus = !!(referralStatus === ReferralStatusIndicator.Statuses.PHARM);
    const canShowEnrollmentMonitoring =
        marketplaceEnhancedEligibility &&
        referralStatus !== ReferralStatusIndicator.Statuses.EXPIRED;
    const isEnrollmentMonitoring = enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING;
    const canFetchOnboardingStatus = !onboardingStatus && !skipFetch;

    const { data: onboardingStatusFromAPI } = useGetOnboardingStatusQuery(
        canShowOnboardingStatus && canFetchOnboardingStatus && referralId ? referralId : skipToken,
    );

    const onboardingStatusToDisplay = onboardingStatus ?? onboardingStatusFromAPI;

    if (canShowEnrollmentMonitoring && isEnrollmentMonitoring) {
        return <EnrollmentStatusIndicator status={EnrollmentStatusIndicator.Statuses.PENDING} />;
    }

    return canShowOnboardingStatus && onboardingStatusToDisplay ? (
        <OnboardingStatusIndicator status={onboardingStatusToDisplay} />
    ) : (
        <ReferralStatusIndicator status={referralStatus} />
    );
};
